import React, {
  memo,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { motion, useReducedMotion } from "framer-motion";
import Header from "../component/Header";
// import { Controls, Player } from "@lottiefiles/react-lottie-player";
import LetsTalk from "../component/LetsTalk";
import Footer from "../component/Footer";
import DataNotFound from "../component/DataNotFound";
import Loader from "../component/Loader";
import { Link } from "react-router-dom";
import formatDate from "../utils/helpers";
import { SEO } from "../component/SEO";
import { getIndexableVideos, buildVideoObject } from "../data/portfolioVideos";
import ButtonArrow from "../component/icons/ButtonArrow";
import NewButton from "../component/NewButton";
import HomeWeAreSection from "../component/section/home/HomeWeAreSection";

const heroEase = [0.16, 1, 0.3, 1];

const revealUp = (delay = 0) => ({
  hidden: { y: "110%" },
  visible: {
    y: 0,
    transition: { duration: 0.85, delay, ease: heroEase },
  },
});

const badgeExpand = (delay = 0) => ({
  hidden: { width: 0 },
  visible: {
    width: "auto",
    transition: { duration: 0.75, delay, ease: heroEase },
  },
});

// const HomeTestimonials = lazy(() => import("../component/HomeTestimonial"));
const HomeFeaturedTestimonial = lazy(
  () => import("../component/section/home/HomeFeaturedTestimonial"),
);
const HomeContactSection = lazy(
  () => import("../component/HomeContactSection"),
);
const HomeEcommerceWins = lazy(() => import("../component/HomeEcommerceWins"));
const HomeClientsLogoGrid = lazy(
  () => import("../component/section/home/HomeClientsLogoGrid"),
  
);

const HomeServicesSection = lazy(
  () => import("../component/section/home/HomeServicesSection"),
);

export default function Home() {
  const prefersReducedMotion = useReducedMotion();
  const [heroReady, setHeroReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const homeTitle =
    "Shopify Agency for Ecommerce Growth | UI/UX, CRO & Development | Abox Agency";
  const homeDescription =
    "Abox Agency helps ecommerce brands grow with Shopify development, UI/UX design, CRO, branding, and website optimization. Explore our work and book a strategy call.";
  const homeUrl = "https://abox.agency/";
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Abox Agency",
        url: "https://abox.agency",
        logo: "https://abox.agency/assets/image/logo.jpg",
        description:
          "Abox Agency is an ecommerce growth agency offering Shopify development, UI/UX design, CRO, branding, and 3D product rendering.",
        sameAs: [
          "https://www.linkedin.com/company/aboxagency/",
          "https://www.instagram.com/aboxagency/",
          "https://dribbble.com/aboxagency/",
          "https://twitter.com/aBox_Agency/",
          "https://www.behance.net/aboxagency",
        ],
        address: {
          "@type": "PostalAddress",
          addressLocality: "Surat",
          addressRegion: "Gujarat",
          addressCountry: "India",
        },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+91-7046216489",
          contactType: "sales",
          availableLanguage: ["English", "Hindi", "Gujarati"],
        },
      },
      {
        "@type": "ProfessionalService",
        name: "Abox Agency",
        url: "https://abox.agency",
        image: "https://abox.agency/assets/image/meta_og_image.jpg",
        description:
          "Shopify development, UI/UX design, CRO, branding, and ecommerce growth services for DTC and retail brands.",
        areaServed: ["IN", "US", "GB", "AE", "AU"],
        serviceType: [
          "Shopify development",
          "UI/UX design",
          "Conversion rate optimization",
          "Branding",
          "Ecommerce website design",
        ],
      },
      // VideoObject(s) now come from src/data/portfolioVideos.js rather than
      // being hardcoded here. The old inline block had two faults Google
      // rejects: embedUrl pointed at the homepage (it must be a player URL,
      // or be omitted when contentUrl is present), and thumbnailUrl was the
      // generic OG card instead of a frame from the showreel.
      //
      // Emitting from the registry also means this schema, the video
      // sitemap and the prerendered HTML cannot drift apart. Nothing is
      // emitted until the showreel is promoted:
      //   npm run videos:posters
      //   npm run videos:promote -- home-showreel
      ...getIndexableVideos("/").map((video) =>
        buildVideoObject(video, homeUrl),
      ),
    ],
  };

  const portfolioLeft = [
    {
      href: "/portfolio/label/pourd-cocktails",
      // onMouseOverColor: { bg: '#184158', text: '#61B0F1' },
      imgSrc: "/assets/image/homeportfolio/cocktails.webp",
      imgAlt: "Pourd cocktail bottle propped against textured rock",
      productVideo:
        "https://d3imeu5zcweet9.cloudfront.net/1756718785262_pour'd.mp4",
      projectName: "Pour’d cocktails",
      projectInfoOne:
        "From glass to screen, every interaction crafted perfectly",
      descriptionOne: "3d Modeling, 3D Rendring, Motion video",
      isFull: false,
      className: "",
    },
    {
      href: "/portfolio/fruzo",
      imgSrc: "/assets/image/homeportfolio/fruzo.webp",
      imgAlt: "Fruzo sachets in a picnic basket with watermelon, pomegranate and iced drinks",
      productVideo:
        "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/fruzo.mp4",
      projectName: "Fruzo",
      projectInfoOne:
        "Making healthy choices simple, one smoothie at a time",
      descriptionOne: "UI/UX Design, Shopify Development, 3D Product Renders, Custom Subscription ",
      isFull: false,
      className: "mt-column",
    },
    {
      href: "/portfolio/label/ras-super-plump",
      // onMouseOverColor: { bg: '#063541', text: '#24A3C3' },
      imgSrc: "/assets/image/homeportfolio/ras.webp",
      imgAlt: "RAS Luxury Oils gold radiance serum with its dropper",
      productVideo:
        "https://d3imeu5zcweet9.cloudfront.net/1756718768709_ras.mp4",
      projectName: "Ras",
      projectInfoOne:
        "Luxury elevated, where skincare meets artistry in every detail.",
      projectInfoTwo: "",
      descriptionOne: "3d Modeling, 3D Rendring, Motion video",
      isFull: true,
      className: "",
    },
    {
      href: "/portfolio/label/my-herb",
      // onMouseOverColor: { bg: '#12144F', text: '#3B9FE6' },
      imgSrc: "/assets/image/homeportfolio/my-herb.webp",
      imgAlt: "MyHerb mushroom gummies jars on a pastel arch set",
      productVideo:
        "https://d3imeu5zcweet9.cloudfront.net/1756718815261_myherb.mp4",
      projectName: "My herb",
      projectInfoOne: "Herbal roots, modern remedies, renewed identity.",
      projectInfoTwo: "",
      descriptionOne: "Branding, Package Design,  3d Modeling, 3D Rendring",
      isFull: false,
      className: "mt-column",
    },
    {
      href: "/portfolio/waylon-smith",
      imgSrc: "/assets/image/homeportfolio/waylon-smith.webp",
      imgAlt: "Waylon Smith",
      productVideo:
        "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/waylon-smith.mp4",
      projectName: "Waylon Smith",
      projectInfoOne: "Luxury eyewear, crafted for a distinctive Dutch aesthetic",
      descriptionOne: "Shopify Development, UI/UX Design, AI Photography, Graphic Design",
      isFull: false,
      className: "",
    },
    {
      href: "/portfolio/uppercase",
      // onMouseOverColor: { bg: '#161616', text: '#EEEEEE' },
      imgSrc: "/assets/image/homeportfolio/uppercase.webp",
      imgAlt: "Traveller carrying an Uppercase backpack beside Tower Bridge",
      productVideo:
        "https://d3imeu5zcweet9.cloudfront.net/1756718704263_uppercase.mp4",
      projectName: "Uppercase",
      projectInfoOne: "Shaping sustainable style into a digital trendsetter.",
      projectInfoTwo: "",
      descriptionOne: "Shopify Store Design and Development, CRO",
      isFull: true,
      className: "",
    },
    {
      href: "/portfolio/anandhaas",
      // onMouseOverColor: { bg: '#4F2D0C', text: '#DD8E17' },
      imgSrc: "/assets/image/anandhaas.webp",
      imgAlt: "Anand Sweets mithai gift box open on a mint green backdrop",
      productVideo:
        "https://s3.in-west3.purestore.io/aboxstore/Case-Study/anandhaas-case-study.webm",
      projectName: "Anandhaas Sweets",
      projectInfoOne:
        "Celebrating the rich heritage of South Indian sweets",
      projectInfoTwo: "",
      descriptionOne: "Shopify Revamp, CRO, UI/UX Design, Graphic Design",
      isFull: false,
      className: "",
    },
    {
      href: "/portfolio/acer-electric",
      imgSrc: "/assets/image/homeportfolio/acer-electric.webp",
      imgAlt: "Acer Electric",
      productVideo:
        "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/acer-electric.mp4",
      projectName: "Acer Electric",
      projectInfoOne:
        "Powering the future of electric mobility",
      descriptionOne: "Website Revamp, Custom Development, UI/UX Design, CRO",
      isFull: false,
      className: "mt-column",
    },
    {
      href: "/portfolio/oval",
      imgSrc: "/assets/image/homeportfolio/oval.webp",
      imgAlt: "Oval skincare bottles, dropper and pill organiser on an orange set",
      productVideo:
        "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/oval.mp4",
      projectName: "Oval",
      projectInfoOne:
        "Shaping the future of pharmaceutical innovation through design",
      descriptionOne: "Brand Identity, Product Design, Pitch Deck, Visual Communication",
      isFull: true,
      className: "",
    },
    {
      href: "/portfolio/eustress",
      imgSrc: "/assets/image/homeportfolio/eustress.webp",
      imgAlt: "Four models in Eustress streetwear posed against a tiled wall",
      productVideo:
        "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/eustress.mp4",
      projectName: "Eustress",
      projectInfoOne:
        "Built for those who keep building",
      descriptionOne: "UI/UX Design, Custom Shopify Development, Performance Optimization",
      isFull: false,
      className: "mt-column",
    },
    {
      href: "/portfolio/elder",
      // onMouseOverColor: { bg: '#194412', text: '#97C75A' },
      imgSrc: "/assets/image/homeportfolio/elder-drinks.webp",
      imgAlt: "Elder sparkling drink cans in a wire basket on green tiles",
      productVideo:
        "https://d3imeu5zcweet9.cloudfront.net/1756718881522_elder.mp4",
      projectName: "Elder drinks",
      projectInfoOne:
        "Light, refreshing, uplifting, flowing as smoothly as their drinks.",
      projectInfoTwo: "",
      descriptionOne:
        "3d Modeling, 3D Rendring, Shopify Store Design and Development",
      isFull: false,
      className: "",
    },
  ];

  const HOME_PORTFOLIO_PAUSE_EVENT = "home-portfolio-pause-others";
  const isMobilePortfolioViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 849.98px)").matches;

  const ProjectOne = memo(
    ({
      href,
      imgSrc,
      imgAlt,
      projectName,
      projectInfoOne,
      projectInfoTwo,
      descriptionOne,
      descriptionTwo,
      productVideo,
      className,
    }) => {
      const videoRef = useRef(null);
      const containerRef = useRef(null);
      const hasLink = Boolean(href);
      const [isPlaying, setIsPlaying] = useState(false);

      useEffect(() => {
        if (!productVideo) return undefined;

        const videoElement = videoRef.current;
        const observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting) {
              if (videoElement && !videoElement.getAttribute("src")) {
                videoElement.src = productVideo;
              }
              observer.unobserve(entry.target);
            }
          },
          {
            rootMargin: "0px 0px 200px 0px",
            threshold: 0,
          },
        );

        const node = containerRef.current;
        if (node) observer.observe(node);

        return () => {
          if (node) observer.unobserve(node);
        };
      }, [productVideo]);

      useEffect(() => {
        if (!productVideo) return undefined;

        const onPauseOthers = (event) => {
          const video = videoRef.current;
          if (!video || event.detail?.except === video) return;
          if (!video.paused) {
            video.pause();
          }
          setIsPlaying(false);
        };

        window.addEventListener(HOME_PORTFOLIO_PAUSE_EVENT, onPauseOthers);
        return () => {
          window.removeEventListener(HOME_PORTFOLIO_PAUSE_EVENT, onPauseOthers);
        };
      }, [productVideo]);

      const pauseOtherPortfolioVideos = () => {
        window.dispatchEvent(
          new CustomEvent(HOME_PORTFOLIO_PAUSE_EVENT, {
            detail: { except: videoRef.current },
          }),
        );
      };

      const handleMouseEnter = () => {
        // Mobile uses the play/pause button — ignore hover simulation.
        if (isMobilePortfolioViewport() || !videoRef.current) return;
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Ignore autoplay failures silently; hover can re-trigger playback.
          });
        }
      };

      const handleMouseLeave = () => {
        if (isMobilePortfolioViewport() || !videoRef.current) return;
        videoRef.current.pause();
        setIsPlaying(false);
      };

      const playVideoWhenReady = (video, { forceLoad = false } = {}) => {
        const tryPlay = () =>
          video
            .play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(() => {
              setIsPlaying(false);
            });

        if (!forceLoad && video.readyState >= 2) {
          return tryPlay();
        }

        const onReady = () => {
          video.removeEventListener("canplay", onReady);
          video.removeEventListener("loadeddata", onReady);
          tryPlay();
        };

        video.addEventListener("canplay", onReady, { once: true });
        video.addEventListener("loadeddata", onReady, { once: true });

        if (forceLoad) {
          video.load();
        }
      };

      const togglePlayback = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const video = videoRef.current;
        if (!video || !productVideo) return;

        if (!video.paused && !video.ended) {
          video.pause();
          setIsPlaying(false);
          return;
        }

        let forceLoad = false;
        if (!video.getAttribute("src")) {
          video.src = productVideo;
          forceLoad = true;
        }

        // Pause any other playing portfolio card, then start this one.
        pauseOtherPortfolioVideos();
        setIsPlaying(true);
        playVideoWhenReady(video, { forceLoad });
      };

      const stopLinkGesture = (event) => {
        // Do not preventDefault — that cancels the following click on some mobile browsers.
        event.stopPropagation();
      };

      const linkClassName = `project-link w-100 ${className || ""} ${
        isPlaying ? "is-playing" : ""
      }`.trim();

      const cardBody = (
        <>
          <div className="position-relative product-img">
            <img
              src={imgSrc}
              loading="lazy"
              alt={imgAlt}
              className="w-100 card-image"
            />
            {productVideo ? (
              <video
                ref={videoRef}
                preload="metadata"
                poster={imgSrc}
                className="card-video"
                muted
                playsInline
                loop
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              ></video>
            ) : null}
            {productVideo ? (
              <button
                type="button"
                className="home-project-video-toggle"
                onPointerDown={stopLinkGesture}
                onClick={togglePlayback}
                aria-label={isPlaying ? "Pause video" : "Play video"}
              >
                {isPlaying ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M6 5h4v14H6V5Zm8 0h4v14h-4V5Z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 5.14v13.72L19.5 12 8 5.14Z" />
                  </svg>
                )}
              </button>
            ) : null}
          </div>
          <div className="div-block-42">
            <p className="product-name change-color color_light">
              {projectName}
            </p>
            <div className="line dark_line mt-0" />
          </div>
          <h3 className="project-info product-info change-color color_light">
            {projectInfoOne} {projectInfoTwo}
          </h3>
          <p className="product-paragraph change-color color_light">
            {descriptionOne} <br className="d-lg-block d-none" />{" "}
            {descriptionTwo}{" "}
          </p>
        </>
      );

      return (
        <div className="anand-sweets" ref={containerRef}>
          {hasLink ? (
            <Link
              to={href}
              className={linkClassName}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {cardBody}
            </Link>
          ) : (
            <div
              className={linkClassName}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {cardBody}
            </div>
          )}
        </div>
      );
    },
  );

  const [blogsData, setBlogsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadBlogs = async () => {
      try {
        const { getBlogs } = await import("../sanity/queries");
        const data = await getBlogs();
        if (!cancelled) setBlogsData(data);
      } catch (err) {
        console.log("Error fetching blogs:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadBlogs();
    return () => {
      cancelled = true;
    };
  }, []);

  const showreelVideoRef = useRef(null);
  const showreelContainerRef = useRef(null);
  const servicesSectionRef = useRef(null);
  const ecommerceSectionRef = useRef(null);
  const clientsSectionRef = useRef(null);
  const testimonialsSectionRef = useRef(null);
  const contactSectionRef = useRef(null);
  const [loadServices, setLoadServices] = useState(false);
  const [loadEcommerce, setLoadEcommerce] = useState(false);
  const [loadClients, setLoadClients] = useState(false);
  const [loadTestimonials, setLoadTestimonials] = useState(true);
  const [loadContact, setLoadContact] = useState(false);
  const SHOWREEL_SRC =
    "https://d3imeu5zcweet9.cloudfront.net/1751611043730__CreativeAgency-PortfolioShowreel_6.mp4";
  const SHOWREEL_POSTER = "/assets/image/meta_og_image.jpg";

  useEffect(() => {
    const videoElement = showreelVideoRef.current;
    const container = showreelContainerRef.current;
    if (!videoElement || !container) return;

    let sourceAttached = false;

    const attachSource = () => {
      if (sourceAttached) return;
      sourceAttached = true;
      videoElement.src = SHOWREEL_SRC;
      videoElement.preload = "auto";
      videoElement.load();
    };

    const tryPlay = () => {
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Ignore autoplay failures; poster stays visible until play succeeds.
        });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          // Start loading early when section is near viewport, then play.
          attachSource();
          tryPlay();
        } else {
          videoElement.pause();
        }
      },
      {
        rootMargin: "600px 0px",
        threshold: 0,
      },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const nodes = [
      {
        ref: servicesSectionRef,
        enable: () => setLoadServices(true),
        rootMargin: "0px 0px 400px 0px",
      },
      {
        ref: ecommerceSectionRef,
        enable: () => setLoadEcommerce(true),
        rootMargin: "0px 0px 200px 0px",
      },
      {
        ref: clientsSectionRef,
        enable: () => setLoadClients(true),
        rootMargin: "0px 0px 400px 0px",
      },
      {
        ref: testimonialsSectionRef,
        enable: () => setLoadTestimonials(true),
        rootMargin: "0px 0px 400px 0px",
      },
      {
        ref: contactSectionRef,
        enable: () => setLoadContact(true),
        rootMargin: "0px 0px 400px 0px",
      },
    ];

    const observers = nodes.map(({ ref, enable, rootMargin }) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            enable();
            observer.unobserve(entry.target);
          });
        },
        { rootMargin, threshold: 0 },
      );
      if (ref.current) observer.observe(ref.current);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <>
      <SEO
        title={homeTitle}
        description={homeDescription}
        url={homeUrl}
        ogTitle="Shopify Development, UI/UX Design, and CRO for Ecommerce Brands | Abox Agency"
        keywords="shopify agency, shopify development agency, ecommerce ui ux design, cro agency, shopify design, ecommerce website development, shopify experts india"
        schema={homeSchema}
      />

      <Header />

      {/* hero section — Figma 9752:3217 */}
      <section className="tw:bg-white tw:px-5 tw:md:px-10 tw:1xl:px-[100px] tw:pt-10 tw:md:pt-14 tw:xl:pt-[100px] tw:pb-12 tw:md:pb-16 tw:xl:pb-[100px] tw:xl:h-[904px]">
        <div className="tw:flex tw:flex-col tw:justify-between tw:gap-12 tw:md:gap-16 tw:xl:gap-20 tw:xl:h-full">
          {/* Badge: body+icon → expand size → text reveal */}
          <div className="tw:overflow-hidden tw:w-fit">
            <motion.div
              initial="hidden"
              animate={prefersReducedMotion || heroReady ? "visible" : "hidden"}
              variants={revealUp(0)}
            >
              <a
                href="https://www.shopify.com/partners/directory/partner/niqox1"
                target="_blank"
                rel="noopener noreferrer"
                className="tw:inline-flex tw:w-fit tw:items-center tw:rounded-full tw:bg-[#417B5A] tw:border tw:border-[#9AB9A7] tw:pl-2 tw:pr-2 tw:py-1.5 tw:lg:pl-5 tw:lg:pr-5 tw:lg:py-3 tw:no-underline tw:hover:opacity-90 tw:overflow-hidden"
              >
                <img
                  src="/assets/image/shopify-partners.svg"
                  alt=""
                  width={35}
                  height={40}
                  className="tw:w-[18px] tw:h-5 tw:lg:w-[35px] tw:lg:h-10 tw:shrink-0"
                  fetchpriority="high"
                  decoding="async"
                  aria-hidden="true"
                />
                <motion.span
                  className="tw:block tw:overflow-hidden"
                  initial="hidden"
                  animate={
                    prefersReducedMotion || heroReady ? "visible" : "hidden"
                  }
                  variants={badgeExpand(0.2)}
                >
                  <span className="tw:flex tw:flex-col tw:whitespace-nowrap tw:pl-1.5 tw:pr-1 tw:lg:pl-[15px] tw:lg:pr-2.5 tw:text-[11px] tw:leading-[1.15] tw:lg:text-[18px] tw:lg:leading-none tw:text-white">
                    <span className="tw:block tw:overflow-hidden">
                      <motion.span
                        className="tw:block tw:font-medium tw:text-white/40"
                        initial="hidden"
                        animate={
                          prefersReducedMotion || heroReady
                            ? "visible"
                            : "hidden"
                        }
                        variants={revealUp(0.35)}
                      >
                        We’re officially
                      </motion.span>
                    </span>
                    <span className="tw:block tw:overflow-hidden">
                      <motion.span
                        className="tw:block tw:font-bold"
                        initial="hidden"
                        animate={
                          prefersReducedMotion || heroReady
                            ? "visible"
                            : "hidden"
                        }
                        variants={revealUp(0.45)}
                      >
                        Shopify Partners
                      </motion.span>
                    </span>
                  </span>
                </motion.span>
              </a>
            </motion.div>
          </div>

          <div className="tw:flex tw:flex-col tw:gap-6 tw:lg:gap-8 tw:xl:gap-12 tw:items-start">
            <div className="tw:flex tw:flex-col tw:gap-4 tw:lg:gap-6 tw:xl:gap-8 tw:items-start">
              <h1 className="tw:m-0 tw:font-medium tw:text-[#222] tw:text-[clamp(28px,7.5vw,48px)] tw:md:text-[clamp(40px,5.5vw,56px)] tw:lg:text-[clamp(56px,6.5vw,128px)] tw:leading-[1.12] tw:lg:leading-[1.15] tw:tracking-[-0.025em]">
                <span className="tw:block tw:overflow-hidden">
                  <motion.span
                    className="tw:block"
                    initial="hidden"
                    animate={
                      prefersReducedMotion || heroReady ? "visible" : "hidden"
                    }
                    variants={revealUp(0.55)}
                  >
                    We make brands to{" "}
                    <span className="tw:text-[#417B5A]">stand</span>
                  </motion.span>
                </span>
                <span className="tw:block tw:overflow-hidden">
                  <motion.span
                    className="tw:block"
                    initial="hidden"
                    animate={
                      prefersReducedMotion || heroReady ? "visible" : "hidden"
                    }
                    variants={revealUp(0.7)}
                  >
                    <span className="tw:text-[#417B5A]">out digital </span>
                    differently.
                  </motion.span>
                </span>
              </h1>
              <div className="tw:overflow-hidden tw:max-w-[935px]">
                <motion.p
                  className="tw:m-0 tw:text-[clamp(14px,3.4vw,17px)] tw:lg:text-[clamp(18px,1.8vw,28px)] tw:leading-[1.5] tw:text-[#222] tw:text-balance"
                  initial="hidden"
                  animate={
                    prefersReducedMotion || heroReady ? "visible" : "hidden"
                  }
                  variants={revealUp(0.85)}
                >
                  We help growing brands scale with Shopify development,
                  conversion-focused UI/UX, ecommerce branding, and performance
                  optimization.
                </motion.p>
              </div>
            </div>

            <div className="tw:overflow-hidden">
              <motion.div
                className="tw:flex tw:flex-wrap tw:items-center tw:gap-2"
                initial="hidden"
                animate={
                  prefersReducedMotion || heroReady ? "visible" : "hidden"
                }
                variants={revealUp(1)}
              >
                <NewButton
                  to="/contact"
                  variant="primary"
                  arrow
                  className="tw:w-fit tw:shrink-0"
                >
                  Let’s Talk
                </NewButton>
                <NewButton
                  to="/portfolio"
                  variant="border"
                  className="tw:w-fit tw:shrink-0"
                >
                  View Case Study
                </NewButton>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* video section */}
      <section id="video" className="section_detail video-section">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 p-0">
              <div className="video" ref={showreelContainerRef}>
                <video
                  ref={showreelVideoRef}
                  poster={SHOWREEL_POSTER}
                  preload="none"
                  loop
                  muted
                  playsInline
                  aria-label="Abox Agency portfolio showreel"
                >
                  <source src={SHOWREEL_SRC} type="video/mp4" />
                  <track
                    kind="captions"
                    src="/assets/captions/showreel-en.vtt"
                    srcLang="en"
                    label="English"
                    default
                  />
                </video>
              </div>
            </div>
            {/* <div className="col-xxl-7 col-xl-6">
                                <div className="div-block-7">
                                    <div className="icon"><img src="/assets/image/icon.svg" loading="lazy" alt="" /></div>
                                    <p className="paragraph-6">Branding, design & scalable e-commerce solutions. <br /> Strategy, technology & design.</p>
                                </div>
                            </div> */}
          </div>
        </div>
      </section>

      <section
        ref={servicesSectionRef}
        className="tw:py-12 tw:md:py-16 tw:xl:py-[100px]"
      >
        <div className="tw:px-5 tw:md:px-10 tw:1xl:px-[100px]">
          <div className="main-service d-sm-flex justify-content-between align-items-end">
            <h2 className="tw:text-[clamp(38px,4.5vw,4rem)] tw:leading-[1.1] tw:font-medium  mb-0">
              Innovative Services <br /> To Drive Growth
            </h2>
            <Link
              to="/services"
              className="servise-button button_bg_green mt-sm-0 mt-3"
            >
              <span>VIEW ALL SERVICES</span>
              <div className="service-box bg-white"></div>
            </Link>
          </div>
          {loadServices ? (
            <Suspense fallback={null}>
              <HomeServicesSection />
            </Suspense>
          ) : null}
        </div>
      </section>

      {/* hire section */}
      <section className="section_detail hire-section">
        <div className="container">
          <div className="row justify-content-between">
            <div className="col-lg-8">
              <p className="paragraph-10">
                100+ brands trust Abox Agency for Shopify development, ecommerce
                UX, CRO, and digital design systems that turn traffic into
                measurable growth.
              </p>
              <div className="div-block-29">
                <Link to="/contact" className="link-block-7">
                  Way to go
                  <img
                    src="/assets/image/right_arrow.svg"
                    loading="lazy"
                    alt=""
                    className="image-4"
                  />
                </Link>
                <div className="div-block-30" />
                <div className="div-block-31" />
                <div className="div-block-32" />
                <div className="div-block-33">
                  <img src="/assets/image/crown.svg" loading="lazy" alt="" />
                </div>
              </div>
            </div>
            <div className="col-lg-3 d-flex justify-content-end">
              <Link
                to="/contact"
                className="hire_link"
                aria-label="Hire us"
                title="Hire us"
              >
                {/* <img src="assets/image/left-top.svg" loading="lazy" alt="" className="image-5 hire_arrow" />
                            <img src="assets/image/right-top.svg" loading="lazy" alt="" className="image-6" />
                            <div className="line_div">
                                <div className="cross_line" />
                            </div>
                            <img src="assets/image/left-bottom.svg" loading="lazy" alt="" className="image-7 hire_arrow" />
                            <img src="assets/image/right-bottom.svg" loading="lazy" alt="" className="image-8 hire_arrow" />
                            <div className="div-block-35">
                                <div className="text-block-6">hire us</div>
                            </div> */}
                <svg
                  id="Component_20_2"
                  data-name="Component 20 – 2"
                  xmlns="http://www.w3.org/2000/svg"
                  width={200}
                  height={200}
                  viewBox="0 0 200 200"
                  aria-hidden="true"
                  focusable="false"
                >
                  <g
                    id="Ellipse_257"
                    data-name="Ellipse 257"
                    transform="translate(0 0)"
                    fill="none"
                    stroke="#222"
                    strokeWidth={1}
                  >
                    <circle cx={100} cy={100} r={100} stroke="none" />
                    <circle cx={100} cy={100} r="99.5" fill="none" />
                  </g>
                  <g
                    id="Ellipse_259"
                    data-name="Ellipse 259"
                    transform="translate(25 0)"
                    fill="none"
                    stroke="#222"
                    strokeWidth={1}
                  >
                    <ellipse cx={75} cy={100} rx={75} ry={100} stroke="none" />
                    <ellipse cx={75} cy={100} rx="74.5" ry="99.5" fill="none" />
                  </g>
                  <g
                    id="Ellipse_260"
                    data-name="Ellipse 260"
                    transform="translate(54 0)"
                    fill="none"
                    stroke="#222"
                    strokeWidth={1}
                  >
                    <ellipse cx={46} cy={100} rx={46} ry={100} stroke="none" />
                    <ellipse cx={46} cy={100} rx="45.5" ry="99.5" fill="none" />
                  </g>
                  <g
                    id="Ellipse_261"
                    data-name="Ellipse 261"
                    transform="translate(118 0) rotate(90)"
                    fill="none"
                    stroke="#222"
                    strokeWidth={1}
                  >
                    <ellipse cx={100} cy={18} rx={100} ry={18} stroke="none" />
                    <ellipse cx={100} cy={18} rx="99.5" ry="17.5" fill="none" />
                  </g>
                  <g
                    id="Ellipse_258"
                    data-name="Ellipse 258"
                    transform="translate(0 70)"
                    fill="#fff"
                    stroke="#222"
                    strokeWidth={1}
                  >
                    <ellipse cx={100} cy={30} rx={100} ry={30} stroke="none" />
                    <ellipse cx={100} cy={30} rx="99.5" ry="29.5" fill="none" />
                  </g>
                  <text
                    id="Hire_Us"
                    data-name="Hire Us"
                    transform="translate(51.657 109.433)"
                    fill="#222"
                    fontSize={35}
                    fontFamily="Ubermove"
                    fontWeight={400}
                  >
                    <tspan x={0} y={0}>
                      hire us
                    </tspan>
                  </text>
                  <path
                    id="Subtraction_6"
                    data-name="Subtraction 6"
                    d="M-1601.017-3392.768h-2.047V-3409.2H-1619.5v-2.047h18.482v18.481Z"
                    transform="translate(1801.017 3411.25)"
                    fill="#222"
                  />
                  <path
                    id="Subtraction_5"
                    data-name="Subtraction 5"
                    d="M-1617.2-3392.768h-2.047v-18.482h18.483v2.047H-1617.2v16.434Z"
                    transform="translate(1619.25 3411.25)"
                    fill="#222"
                  />
                  <path
                    id="Subtraction_4"
                    data-name="Subtraction 4"
                    d="M-1600.768-3393.017h-18.482V-3411.5h2.047v16.434h16.435v2.048Z"
                    transform="translate(1619.25 3593.017)"
                    fill="#222"
                  />
                  <path
                    id="Subtraction_3"
                    data-name="Subtraction 3"
                    d="M-1601.518-3393.518H-1619.5v-1.547h16.435V-3411.5h1.547v17.981Z"
                    transform="translate(1801.518 3593.518)"
                    fill="#222"
                  />
                  <path
                    id="Path_51"
                    data-name="Path 51"
                    d="M159.887,1.185l-17.3,17.3"
                    transform="translate(39.057 0)"
                    fill="none"
                    stroke="#222"
                    strokeWidth={2}
                  />
                </svg>
                <span className="visually-hidden">Hire us</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* <div className="marquee-client-section bg-white overflow-hidden w-100">
                    <div className="container">
                        <div className="d-flex flex-column gap-30">
                            <h3 className="partners-title white_space mb-0">Trusted by 200+ client across the world</h3>
                            <div className="marquee-slider position-relative">
                                <div className="partners_marquee">
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/nourysh.svg" alt="Nourysh brand logo in the client trust strip" className="w-100" />
                                    </div>
                                    <div className="client_logo p_30">
                                        <img src="/assets/image/clients/bombay-sweets.svg" alt="Bombay Sweets brand logo among Abox Agency clients" className="w-100" />
                                    </div>
                                    <div className="client_logo p_30">
                                        <img src="/assets/image/clients/d_samie.svg" alt="D Samie brand logo shown as a client mark" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/helthoxide.svg" alt="Helthoxide brand logo in the client roster" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/zamron.svg" alt="Zamron brand logo with its green leaf detail" className="w-100" />
                                    </div>
                                    <div className="client_logo p_30">
                                        <img src="/assets/image/clients/u_bar.svg" alt="U Bar brand logo in orange lettering" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/shistaka.svg" alt="Shistaka brand logo with a small leaf above the wordmark" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/pushti.svg" alt="Pushti brand logo in green script" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/mountowl.svg" alt="MountOwl brand logo listed with other clients" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/onlyplants.svg" alt="Onlyplants brand logo with a leaf mark" className="w-100" />
                                    </div>
                                    <div className="client_logo">
                                        <img src="/assets/image/clients/kloy.svg" alt="Kloy brand logo closing the client strip" className="w-100" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> */}

      {/* project section */}
      <section id="project" className="section_detail project-section">
        <div className="container">
          <div className="row project-detail align-items-end">
            <div className="col-xl-7 col-lg-10 col-12 d-flex flex-column gap-30">
              <h2 className="heading-6 lh-100 change-color">
                We let our work <br className="d-xl-block d-none" /> speak for
                itself.
              </h2>
              <p className="paragraph-11 lh-100 font_light change-color">
                Explore our portfolio to see how we turn ideas into results,
                creativity into impact, and vision into awards.
              </p>
            </div>
            {/* <div className="col-lg-5 text-end">
                                <Link to="#" className="servise-button bg-white">
                                    <span className="text_black">VIEW ALL SERVICES</span>
                                    <div className="service-box bg_dark buttonbox"></div>
                                </Link>
                            </div> */}
          </div>
          <div className="div-block-40 flex-column">
            <div className="porfolio-grid">
              {/* <div className="projects me-0"> */}
              {portfolioLeft.map((i) => (
                <div
                  key={i.projectName}
                  className={`project-item ${i.isFull ? "full" : ""}`}
                >
                  <ProjectOne {...i} />
                </div>
              ))}

              {/* <div className="anand-sweets">
                                                <div className="project-link d-flex flex-column align-items-start gap-30">
                                                    <h3 className="service_h2 color_light change-color lh-100 font_medium">
                                                        There's more <br className="d-md-block d-none" />
                                                        stories to see
                                                    </h3>
                                                    <Link to="/portfolio" className="servise-button bg-white">
                                                        <span className="text_black">VIEW ALL PROJECTS</span>
                                                        <div className="service-box bg_dark buttonbox"></div>
                                                    </Link>
                                                    <Link to="/portfolio" className="btn submit_btn all_portfolio mt-4">
                                                        View all projects
                                                        <svg className="ms-2 submit_arrow arrow_m_width" xmlns="http://www.w3.org/2000/svg" width="18.412" height="18.368" viewBox="0 0 18.412 18.368">
                                                            <path id="Path_56" data-name="Path 56" d="M0,16.953,14.954,2H8.078V0H18.412V10.335h-2V3.37l-15,15Z" fill="#222222" />
                                                        </svg>
                                                        <svg className="ms-2 submit_hover arrow_m_width" xmlns="http://www.w3.org/2000/svg" width="18.412" height="18.368" viewBox="0 0 18.412 18.368">
                                                            <path id="Path_56" data-name="Path 56" d="M0,16.953,14.954,2H8.078V0H18.412V10.335h-2V3.37l-15,15Z" fill="#EFEBE7" />
                                                        </svg>
                                                    </Link>
                                                </div>
                                            </div>  */}
              {/* </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* seven plus section */}
      <section className="section_detail">
        <div className="container">
          <div className="approach bg_svg">
            <div className="row">
              <div className="col-lg-5 approach_div d-flex flex-column align-items-start justify-content-lg-between">
                <div>
                  <p className="sevenplus_p mb-md-4 mb-3">Been in the game</p>
                  <h2 className="sevenplus_h2">
                    Eight<sup className="color_dark">+</sup>
                    <br /> Years
                  </h2>
                </div>
                {/* <Link to="#" className="servise-button button_bg_green mt-lg-0 mt-4">
                                        <span>SHOW ME THE PROCESS</span>
                                        <div className="service-box bg-white"></div>
                                    </Link> */}
              </div>
              <div className="col-lg-5 col-md-9 offset-lg-2 partnership d-flex flex-column justify-content-end content_margin">
                <p>
                  We bring strategy, Shopify development, UI/UX design, CRO,
                  branding, and creative production together so your team can
                  move from idea to launch without juggling multiple vendors.
                </p>
              </div>
            </div>
            {/* <div className="svg_pattern">
                                <svg className="d-none d-md-block" xmlns="http://www.w3.org/2000/svg" width="1720" height="727" viewBox="0 0 1720 727" fill="none">
                                    <line opacity="0.3" x1="763" y1="538.5" x2="896" y2="538.5" stroke="#222222" />
                                    <line opacity="0.3" x1="764" y1="506.5" x2="814" y2="506.5" stroke="#222222" />
                                    <line opacity="0.3" x1="796" y1="522.5" x2="814" y2="522.5" stroke="#222222" />
                                    <line opacity="0.3" x1="763.5" y1="537.996" x2="764.21" y2="456.996" stroke="#222222" />
                                    <line opacity="0.3" x1="813.5" y1="537.996" x2="814.21" y2="456.996" stroke="#222222" />
                                    <line opacity="0.3" x1="796.5" y1="538" x2="796.5" y2="506" stroke="#222222" />
                                    <circle cx="1617.5" cy="354.714" r="51.75" stroke="#417B5A" stroke-width="1.5" />
                                    <line opacity="0.3" x1="895.5" y1="727" x2="895.5" y2="-2.18557e-08" stroke="#222222" />
                                    <line opacity="0.3" x1="4.37114e-08" y1="456.714" x2="1720" y2="456.714" stroke="#222222" />
                                    <rect x="1546.75" y="283.964" width="27.5" height="27.5" stroke="#417B5A" stroke-width="1.5" />
                                    <path d="M1452.98 669.977C1452.98 361.825 1203.35 112.022 895.405 112.022" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M551.022 456.641C551.022 266.313 705.207 112.022 895.405 112.022" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M551.022 456.641C551.022 574.465 646.468 669.977 764.212 669.977" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M895.405 538.695C895.405 611.198 836.666 669.978 764.212 669.978" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M895.405 538.695C895.405 493.373 858.698 456.641 813.407 456.641" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M764.212 505.869C764.212 478.688 786.245 456.641 813.407 456.641" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M764.212 505.869C764.212 523.99 778.887 538.695 797.016 538.695" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M813.407 522.271C813.407 531.332 806.07 538.674 797.016 538.674" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <rect x="896" y="457" width="824" height="215" fill="white" />
                                </svg>
                                <svg className="d-block d-md-none" xmlns="http://www.w3.org/2000/svg" width="451" height="757" viewBox="0 0 451 757" fill="none">
                                    <line opacity="0.3" y1="-0.5" x2="86.45" y2="-0.5" transform="matrix(1.30477e-07 -1 -1 -1.30477e-07 160.425 589.05)" stroke="#222222" />
                                    <line opacity="0.3" y1="-0.5" x2="32.5" y2="-0.5" transform="matrix(1.31134e-07 -1 -1 -1.31134e-07 181.225 588.4)" stroke="#222222" />
                                    <line opacity="0.3" y1="-0.5" x2="11.7" y2="-0.5" transform="matrix(1.31134e-07 -1 -1 -1.31134e-07 170.825 567.6)" stroke="#222222" />
                                    <line opacity="0.3" y1="-0.5" x2="52.652" y2="-0.5" transform="matrix(0.999962 -0.0087646 -0.0087646 -0.999962 161.075 588.4)" stroke="#222222" />
                                    <line opacity="0.3" y1="-0.5" x2="52.652" y2="-0.5" transform="matrix(0.999962 -0.0087646 -0.0087646 -0.999962 161.075 555.9)" stroke="#222222" />
                                    <line opacity="0.3" y1="-0.5" x2="20.8" y2="-0.5" transform="matrix(1 8.74228e-08 8.74228e-08 -1 161.075 566.95)" stroke="#222222" />
                                    <circle cx="34.125" cy="34.125" r="33.375" transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 314.336 97.25)" stroke="#417B5A" stroke-width="1.5" />
                                    <line opacity="0.3" x1="4.37114e-08" y1="502.5" x2="451" y2="502.5" stroke="#222222" />
                                    <line opacity="0.3" x1="213.5" y1="757" x2="213.5" y2="-2.18557e-08" stroke="#222222" />
                                    <rect x="-0.75" y="-0.75" width="17.35" height="17.35" transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 325.186 108.1)" stroke="#417B5A" stroke-width="1.5" />
                                    <path d="M75.2901 140.564C275.589 140.564 437.961 302.825 437.961 502.987" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M213.959 726.836C337.672 726.836 437.961 626.615 437.961 502.987" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M213.959 726.835C137.373 726.835 75.2902 664.795 75.2902 588.262" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M160.623 502.987C113.496 502.987 75.2898 541.167 75.2898 588.262" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M160.624 502.987C190.083 502.987 213.959 526.846 213.959 556.285" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M181.96 588.261C199.628 588.261 213.959 573.94 213.959 556.285" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M181.96 588.262C170.182 588.262 160.624 578.723 160.624 566.939" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                    <path d="M171.299 556.285C165.41 556.285 160.637 561.054 160.637 566.939" stroke="#AA5042" stroke-width="1.5" stroke-miterlimit="10" />
                                </svg>
                            </div> */}
          </div>
        </div>
      </section>

      {/* <div className="process">
                    <div className="container">
                        <div className="position-relative">
                            <a href="#" className="big_btn mx-auto">Show me the process</a>
                            <div className="border_multi">
                                <svg xmlns="http://www.w3.org/2000/svg" width={1748} height={670} viewBox="0 0 1748 670" fill="none">
                                    <rect x="230.5" y="230.5" width={1287} height={209} rx="104.5" stroke="#222222" strokeOpacity="0.3" />
                                    <rect x="130.5" y="130.5" width={1487} height={409} rx="199.5" stroke="#222222" strokeOpacity="0.2" />
                                    <rect x="0.5" y="0.5" width={1747} height={669} rx="334.5" stroke="#222222" strokeOpacity="0.1" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div> */}

      {/* E-Commerce Wins section */}
      {/*<section className="section_detail e-commerce-section">
                    <div className="container">
                        <div className="stores-banner">
                            <h2 className="banner-title">WE</h2>
                            <div className="store-bag">
                                <img src="/assets/image/store-bag.png" loading="lazy" alt="Silver 3D shopping bag carrying the Shopify letter S mark" className="w-100" />
                            </div>
                        </div> 
                    </div>
                </section>*/}

      <div
        ref={ecommerceSectionRef}
        className={loadEcommerce ? undefined : "ecomm-lazy-slot"}
      >
        {loadEcommerce ? (
          <Suspense fallback={null}>
            <HomeEcommerceWins />
          </Suspense>
        ) : null}
      </div>

      <section className="section_detail one_roof_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="d-flex flex-column store-gap">
                <h2 className="one_roof_title mb-0">
                  One Roof. <br /> Zero Hassle. <br /> Faster Launch.
                </h2>
                <p className="sevenplus_p">
                  Why juggle between multiple vendors when you can have it all
                  under one expert roof? From branding, UI/UX design, and
                  development to graphics, 3D, and marketing support we handle
                  everything, in-house.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HomeWeAreSection />

      {/* testimonial section — old Shopify carousel (kept for reference)
            <section ref={testimonialsSectionRef} className="section_detail testimonial_section overflow-hidden">
                <div className="container">
                    <div className="row">
                        <div className="col-xl-7">
                            <h2 className="paragraph-7 mb-0">Client speak out <br /> about us on
                                <span className="shopify_logo position-relative"><ShopifyBag /></span>
                                Shopify
                            </h2>
                        </div>
                    </div>
                    {loadTestimonials ? (
                        <Suspense fallback={null}>
                            <HomeTestimonials />
                        </Suspense>
                    ) : null}
                </div>
            </section>
            */}

      {/* featured client testimonial — Figma 9942:7204–7434 */}
      <div ref={testimonialsSectionRef} className="tw:min-h-px">
        {loadTestimonials ? (
          <Suspense fallback={null}>
            <HomeFeaturedTestimonial />
          </Suspense>
        ) : null}
      </div>

      {/* client section */}
      <div ref={clientsSectionRef}>
        {loadClients ? (
          <Suspense fallback={null}>
            <HomeClientsLogoGrid />
          </Suspense>
        ) : null}
      </div>

      {/* Featured News Section */}
      <section className="section_detail news_section">
        <div className="container">
          <div className="d-flex flex-md-row flex-column justify-content-between align-items-md-end align-items-strat">
            <div>
              <h2 className="paragraph-7 mb-0">Featured News</h2>
            </div>
            <div className="d-flex logo_btn mt-md-0 mt-4">
              <Link to={"/blog"} className="btn submit_btn">
                News Overview
                <ButtonArrow
                  className="ms-2 submit_arrow arrow_m_width"
                  fill={"#222222"}
                />
                <ButtonArrow
                  className="ms-2 submit_hover arrow_m_width"
                  fill={"#EFEBE7"}
                />
              </Link>
            </div>
          </div>
          {!loading ? (
            blogsData.length ? (
              blogsData?.slice(0, 2).map((i, index) => (
                <Link
                  key={i.id || index}
                  to={`/blog/${i.slug}`}
                  className="text-decoration-none"
                >
                  <hr className="news_line" />
                  <div className="news_box">
                    <div className="position-relative d-inline-block w-100">
                      <img
                        src={i.thumbnail}
                        className="news_img"
                        alt={i.title}
                        loading="lazy"
                      />
                      <div className="news_link w-100">
                        <div className="d-flex justify-content-between align-items-center news_padding w-100">
                          <div>
                            <p className="news_btn">{i.categories[0]}</p>
                          </div>
                          <div className="new-date">
                            <span>{formatDate(i.createdAt)}</span>
                          </div>
                        </div>
                        <div className="d-flex flex-xl-row flex-column justify-content-between align-items-center">
                          <div className="news_title">
                            <h3>{i.title}</h3>
                          </div>
                          <div className="news_title">
                            <p>{i.excerpt}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <DataNotFound />
            )
          ) : (
            <Loader />
          )}
        </div>
      </section>

      <div ref={contactSectionRef}>
        {loadContact ? (
          <Suspense fallback={null}>
            <HomeContactSection inquiryFrom="Home Page" />
          </Suspense>
        ) : null}
      </div>

      <LetsTalk />

      <Footer />
    </>
  );
}
